import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import {
	Stage,
	Layer,
	Text as KonvaText,
	Transformer,
	Rect,
	Group,
} from "react-konva";
import { BasicModal } from "./BasicModal/BasicModal";
import ReactQuill from "react-quill";
import { Edit } from "@mui/icons-material";

const TextSticker = React.memo(
	({
		text,
		index,
		isSelected,
		visible,
		onSelect,
		onClick,
		updateCurrentPage,
		page,
		context,
		setTextIndex,
		setModalText,
		setIsModalOpen,
		handleTextClick,
		onDelete,
		tabbar,
		handleContextMenu,
		onTransformStart,
		onTransformEnd,
		getCanvasWidth,
		getCanvasHeight,
		canvasWidth: canvasWidthProp,
		canvasHeight: canvasHeightProp,
		// Nuevas props para posicionamiento exacto (igual que Sticker)
		displayX,
		displayY,
		blockRects,
		hitTestBlock,
	}) => {
	const textRef = useRef();
	const trRef = useRef();
	const [width, setWidth] = useState(0);
	const [height, setHeight] = useState(0);
	const [firstTime, setFirstTime] = useState(true);
	const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
	const resizeTimeoutRef = useRef(null);
	const didMigrateAnchorRef = useRef(false);
	const didMigrateFontSizePercentRef = useRef(false);

	// Refs para detectar scroll durante touch
	const touchStartRef = useRef(null);
	const hasMovedRef = useRef(false);

		// Efecto para actualizar cuando cambie el tamaño del canvas (para que el anclaje re-renderice)
		React.useEffect(() => {
			const updateCanvasSize = () => {
				if (getCanvasWidth && getCanvasHeight) {
					if (resizeTimeoutRef.current) {
						cancelAnimationFrame(resizeTimeoutRef.current);
					}
					resizeTimeoutRef.current = requestAnimationFrame(() => {
						const newWidth = getCanvasWidth();
						const newHeight = getCanvasHeight();
						setCanvasSize((prev) => {
							if (prev.width !== newWidth || prev.height !== newHeight) {
								return { width: newWidth, height: newHeight };
							}
							return prev;
						});
					});
				}
			};

			updateCanvasSize();

			let resizeObserver = null;
			try {
				resizeObserver = new ResizeObserver(() => {
					updateCanvasSize();
				});
				const canvas = document.querySelector(".konvajs-content > canvas");
				const templateContainer = document.getElementById("templates-container");
				const targetElement = canvas || templateContainer;
				if (targetElement) {
					resizeObserver.observe(targetElement);
				}
			} catch (error) {
				console.warn("ResizeObserver error:", error);
			}

			const handleWindowResize = () => updateCanvasSize();
			window.addEventListener("resize", handleWindowResize);

			return () => {
				if (resizeTimeoutRef.current) {
					cancelAnimationFrame(resizeTimeoutRef.current);
				}
				if (resizeObserver) resizeObserver.disconnect();
				window.removeEventListener("resize", handleWindowResize);
			};
		}, [getCanvasWidth, getCanvasHeight]);

		// Misma lógica que Sticker: corners y medio (el anclaje más cercano por tercios)
		const getCanvasDims = React.useCallback(() => {
			if (canvasWidthProp != null && canvasHeightProp != null) {
				return { w: canvasWidthProp, h: canvasHeightProp };
			}
			return {
				w: getCanvasWidth ? getCanvasWidth() : canvasSize.width || 800,
				h: getCanvasHeight ? getCanvasHeight() : canvasSize.height || 1000,
			};
		}, [canvasWidthProp, canvasHeightProp, getCanvasWidth, getCanvasHeight, canvasSize.width, canvasSize.height]);

		// Solo 8 puntos del borde (4 esquinas + 4 centros de lado), sin centro del canvas
		const detectAnchor = React.useCallback(
			(pixelX, pixelY, elementWidth, elementHeight, cw, ch) => {
				const canvasWidth = cw ?? getCanvasDims().w;
				const canvasHeight = ch ?? getCanvasDims().h;
				const centerX = pixelX + elementWidth / 2;
				const centerY = pixelY + elementHeight / 2;
				const borderPoints = [
					{ anchor: "top-left", x: 0, y: 0 },
					{ anchor: "top-center", x: canvasWidth / 2, y: 0 },
					{ anchor: "top-right", x: canvasWidth, y: 0 },
					{ anchor: "center-left", x: 0, y: canvasHeight / 2 },
					{ anchor: "center-right", x: canvasWidth, y: canvasHeight / 2 },
					{ anchor: "bottom-left", x: 0, y: canvasHeight },
					{ anchor: "bottom-center", x: canvasWidth / 2, y: canvasHeight },
					{ anchor: "bottom-right", x: canvasWidth, y: canvasHeight },
				];
				let minD2 = Infinity;
				let best = borderPoints[0];
				for (const p of borderPoints) {
					const d2 = (centerX - p.x) ** 2 + (centerY - p.y) ** 2;
					if (d2 < minD2) {
						minD2 = d2;
						best = p;
					}
				}
				return best.anchor;
			},
			[getCanvasDims]
		);

		// Offset en píxeles desde el punto de anclaje (esquina/centro más cercano en la rejilla 3x3).
		// Así "50px de la esquina superior derecha" se mantiene al redimensionar.
		const getPositionFromAnchor = React.useCallback(
			(anchor, offsetX, offsetY, elementWidth, elementHeight, cw, ch, offsetInPixels) => {
				const canvasWidth = cw ?? getCanvasDims().w;
				const canvasHeight = ch ?? getCanvasDims().h;
				const px = (val, dim) => (offsetInPixels ? val : (val / 100) * dim);
				let pixelX, pixelY;
				if (anchor.includes("left")) {
					pixelX = px(offsetX, canvasWidth);
				} else if (anchor.includes("right")) {
					const rightEdge = canvasWidth - px(offsetX, canvasWidth);
					pixelX = rightEdge - elementWidth;
				} else if (anchor.includes("center") || anchor === "center") {
					const centerX = canvasWidth / 2 + px(offsetX, canvasWidth);
					pixelX = centerX - elementWidth / 2;
				} else {
					pixelX = px(offsetX, canvasWidth);
				}
				if (anchor.includes("top")) {
					pixelY = px(offsetY, canvasHeight);
				} else if (anchor.includes("bottom")) {
					const bottomEdge = canvasHeight - px(offsetY, canvasHeight);
					pixelY = bottomEdge - elementHeight;
				} else if (anchor === "center" || anchor.includes("center")) {
					const centerY = canvasHeight / 2 + px(offsetY, canvasHeight);
					pixelY = centerY - elementHeight / 2;
				} else {
					pixelY = px(offsetY, canvasHeight);
				}
				return { x: pixelX, y: pixelY };
			},
			[getCanvasDims]
		);

		// Guardar distancia en PÍXELES al punto de anclaje (el más cercano en la rejilla 3x3).
		const pixelToAnchor = React.useCallback(
			(pixelX, pixelY, elementWidth, elementHeight, cw, ch) => {
				const canvasWidth = cw ?? getCanvasDims().w;
				const canvasHeight = ch ?? getCanvasDims().h;
				const anchor = detectAnchor(pixelX, pixelY, elementWidth, elementHeight, canvasWidth, canvasHeight);
				let offsetX, offsetY;
				if (anchor.includes("left")) {
					offsetX = pixelX;
				} else if (anchor.includes("right")) {
					offsetX = canvasWidth - (pixelX + elementWidth);
				} else if (anchor.includes("center") || anchor === "center") {
					offsetX = pixelX + elementWidth / 2 - canvasWidth / 2;
				} else {
					offsetX = pixelX;
				}
				if (anchor.includes("top")) {
					offsetY = pixelY;
				} else if (anchor.includes("bottom")) {
					offsetY = canvasHeight - (pixelY + elementHeight);
				} else if (anchor === "center" || anchor.includes("center")) {
					offsetY = pixelY + elementHeight / 2 - canvasHeight / 2;
				} else {
					offsetY = pixelY;
				}
				return { anchor, offsetX, offsetY, offsetInPixels: true, canvasWidth, canvasHeight };
			},
			[detectAnchor, getCanvasDims]
		);

		const measureTextBox = React.useCallback(
			(value) => {
				const ctx = context;
				const fontSize = text.fontSize ?? 16;
				const fontFamily = text.fontFamily ?? "Montserrat";
				const strokeWidth = text.strokeWidth ?? 0;
				const paddingX = 20;
				const paddingY = 10;

				const lines = String(value ?? "").split("\n");
				const longestLine = lines.reduce(
					(acc, line) => (String(line).length > String(acc).length ? line : acc),
					""
				);

				if (ctx) {
					try {
						ctx.font = `${fontSize}px "${fontFamily}"`;
						ctx.textBaseline = "top";
					} catch (e) {
						// no-op: si el contexto no permite setear font, usamos fallback simple
					}
				}

				const measuredWidth =
					(ctx?.measureText?.(longestLine)?.width ?? longestLine.length * fontSize) +
					strokeWidth * 2 +
					paddingX;
				const lineHeight = fontSize * 1.2;
				const measuredHeight =
					lineHeight * lines.length + strokeWidth * 2 + paddingY;

				return {
					width: Math.max(30, measuredWidth || 30),
					height: Math.max(10, measuredHeight || 10),
				};
			},
			[
				context,
				text.fontFamily,
				text.fontSize,
				text.strokeWidth,
			]
		);

		React.useEffect(() => {
			if (isSelected) {
				try {
					trRef.current.nodes([textRef.current]);
					trRef.current.getLayer().batchDraw();
				} catch (error) {
					console.log(error);
				}
			}
		}, [isSelected, textRef]);

		React.useEffect(() => {
			// Si faltan width/height (datos antiguos), calcularlos con la fuente correcta.
			if (textRef.current && firstTime && (!text.width || !text.height)) {
				const auxPage = { ...page };
				const updatedTexts = auxPage.canvasTexts.slice();
				const dims = measureTextBox(text.text);
				updatedTexts[index] = {
					...updatedTexts[index],
					width: dims.width,
					height: dims.height,
				};

				auxPage.canvasTexts = [...updatedTexts];
				updateCurrentPage(auxPage);

				setFirstTime(false);
				textRef.current.getLayer()?.batchDraw();
			} else if (textRef.current && firstTime) {
				// No tocar valores existentes, solo marcar como inicializado.
				setFirstTime(false);
			}
		}, [text.text, text.width, text.height, firstTime, textRef, index, page, updateCurrentPage, measureTextBox]);

		React.useEffect(() => {
			// Importante: cuando se cargan las fuentes, Konva NO recalcula métricas internas del texto
			// automáticamente. Esto causa que el texto se "desplace" dentro de su box tras reload.
			let cancelled = false;
			const refreshTextMetrics = () => {
				if (cancelled) return;
				const node = textRef.current;
				if (!node) return;
				// Forzar a Konva a recomputar el layout del texto con la fuente ya cargada
				try {
					node.fontFamily(node.fontFamily());
					node.fontSize(node.fontSize());
					node.text(node.text());
				} catch (e) {
					// no-op
				}
				node.getLayer()?.batchDraw();
			};

			if (document?.fonts?.ready?.then) {
				document.fonts.ready.then(refreshTextMetrics);
			} else {
				// Fallback para navegadores sin Font Loading API
				setTimeout(refreshTextMetrics, 0);
			}
			return () => {
				cancelled = true;
			};
		}, []);

		// Mismas dimensiones que el Stage: 550→350 hace bounding box 200→200*350/550
		const canvasW =
			canvasWidthProp != null && canvasHeightProp != null
				? canvasWidthProp
				: getCanvasWidth
				? getCanvasWidth()
				: canvasSize.width || 800;
		const canvasH =
			canvasWidthProp != null && canvasHeightProp != null
				? canvasHeightProp
				: getCanvasHeight
				? getCanvasHeight()
				: canvasSize.height || 1000;
		const resolvedDims =
			text.widthPercent != null && text.heightPercent != null && canvasW > 0 && canvasH > 0
				? {
						width: (text.widthPercent / 100) * canvasW,
						height: (text.heightPercent / 100) * canvasH,
				  }
				: text.width && text.height
				? { width: text.width, height: text.height }
				: measureTextBox(text.text);

		// fontSize ligado a la template cuando hay fontSizePercent
		const resolvedFontSize =
			text.fontSizePercent != null && canvasW > 0
				? (text.fontSizePercent / 100) * canvasW
				: (text.fontSize ?? 16);

		// Dimensiones "visibles" (considerando scale)
		const visibleWidth = resolvedDims.width * (text.scaleX || 1);
		const visibleHeight = resolvedDims.height * (text.scaleY || 1);

		// Convertir anclaje a píxeles (misma fuente de dimensiones que el Stage)
		const getPixelX = () => {
			if (
				text.anchor &&
				text.offsetX !== undefined &&
				text.offsetY !== undefined
			) {
				return getPositionFromAnchor(
					text.anchor,
					text.offsetX,
					text.offsetY,
					visibleWidth,
					visibleHeight,
					canvasW,
					canvasH,
					text.offsetInPixels
				).x;
			}
			if (text.xPercent !== undefined) {
				return (text.xPercent / 100) * canvasW;
			}
			return text.x || 0;
		};

		const getPixelY = () => {
			if (
				text.anchor &&
				text.offsetX !== undefined &&
				text.offsetY !== undefined
			) {
				return getPositionFromAnchor(
					text.anchor,
					text.offsetX,
					text.offsetY,
					visibleWidth,
					visibleHeight,
					canvasW,
					canvasH,
					text.offsetInPixels
				).y;
			}
			if (text.yPercent !== undefined) {
				return (text.yPercent / 100) * canvasH;
			}
			return text.y || 0;
		};

		// Usar displayX/displayY del padre si están definidos (igual que Sticker)
		// Esto asegura que el texto se posicione exactamente donde se soltó
		const useDisplayPos = displayX !== undefined && displayY !== undefined;
		const pixelX = useDisplayPos ? displayX : getPixelX();
		const pixelY = useDisplayPos ? displayY : getPixelY();

		// Migración: si el texto no tiene anclaje, generarlo una sola vez (como stickers)
		React.useEffect(() => {
			if (didMigrateAnchorRef.current) return;
			if (
				text.anchor &&
				text.offsetX !== undefined &&
				text.offsetY !== undefined
			) {
				didMigrateAnchorRef.current = true;
				return;
			}
			// Solo migrar si tenemos dimensiones válidas y canvas medible
			if (!getCanvasWidth || !getCanvasHeight) return;
			if (!resolvedDims.width || !resolvedDims.height) return;

			const anchorData = pixelToAnchor(pixelX, pixelY, visibleWidth, visibleHeight, canvasW, canvasH);
			const auxPage = { ...page };
			const updatedTexts = auxPage.canvasTexts.slice();
			const cw = anchorData.canvasWidth;
			const ch = anchorData.canvasHeight;
			updatedTexts[index] = {
				...updatedTexts[index],
				anchor: anchorData.anchor,
				offsetX: anchorData.offsetX,
				offsetY: anchorData.offsetY,
				offsetInPixels: anchorData.offsetInPixels ?? true,
				xPercent: (pixelX / cw) * 100,
				yPercent: (pixelY / ch) * 100,
				x: pixelX,
				y: pixelY,
				widthPercent:
					cw > 0 ? (visibleWidth / cw) * 100 : undefined,
				heightPercent:
					ch > 0 ? (visibleHeight / ch) * 100 : undefined,
				fontSizePercent:
					text.fontSize != null && cw > 0
						? (text.fontSize / cw) * 100
						: undefined,
			};
			auxPage.canvasTexts = [...updatedTexts];
			updateCurrentPage(auxPage);
			didMigrateAnchorRef.current = true;
		}, [
			text.anchor,
			text.offsetX,
			text.offsetY,
			getCanvasWidth,
			getCanvasHeight,
			resolvedDims.width,
			resolvedDims.height,
			visibleWidth,
			visibleHeight,
			pixelX,
			pixelY,
			pixelToAnchor,
			page,
			index,
			updateCurrentPage,
		]);

		// Migrar fontSizePercent cuando falte (textos que ya tenían anchor)
		React.useEffect(() => {
			if (didMigrateFontSizePercentRef.current) return;
			if (text.fontSizePercent != null) {
				didMigrateFontSizePercentRef.current = true;
				return;
			}
			if (text.fontSize == null || !getCanvasWidth) return;
			const cw = getCanvasWidth();
			if (cw <= 0) return;
			const auxPage = { ...page };
			const updatedTexts = auxPage.canvasTexts.slice();
			updatedTexts[index] = {
				...updatedTexts[index],
				fontSizePercent: (text.fontSize / cw) * 100,
			};
			auxPage.canvasTexts = [...updatedTexts];
			updateCurrentPage(auxPage);
			didMigrateFontSizePercentRef.current = true;
		}, [text.fontSize, text.fontSizePercent, getCanvasWidth, page, index, updateCurrentPage]);

		// Handler para detectar si hubo scroll antes de ejecutar el tap
		const handleSelect = (e) => {
			// Si hubo movimiento significativo (scroll), no ejecutar el select
			if (hasMovedRef.current) {
				hasMovedRef.current = false;
				touchStartRef.current = null;
				return;
			}
			e.cancelBubble = true;
			// Si está seleccionado, deseleccionar. Si no, seleccionar.
			onSelect(isSelected);
		};

		// Handler para touchstart - rastrear posición inicial
		const handleTouchStart = (e) => {
			const touch = e.evt?.touches?.[0] || e.evt?.changedTouches?.[0];
			if (touch) {
				touchStartRef.current = {
					x: touch.clientX,
					y: touch.clientY,
					time: Date.now()
				};
				hasMovedRef.current = false;
			}
		};

		// Handler para touchmove - detectar si hay movimiento
		const handleTouchMove = (e) => {
			if (touchStartRef.current) {
				const touch = e.evt?.touches?.[0] || e.evt?.changedTouches?.[0];
				if (touch) {
					const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
					const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
					// Si el movimiento es mayor a 5px, considerar que hubo scroll
					if (deltaX > 5 || deltaY > 5) {
						hasMovedRef.current = true;
					}
				}
			}
		};

		// Handler para touchend - resetear estado después de un delay
		const handleTouchEnd = () => {
			// Resetear después de un breve delay para permitir que onClick/onTap se ejecuten primero
			setTimeout(() => {
				hasMovedRef.current = false;
				touchStartRef.current = null;
			}, 100);
		};

		return (
			<>
				{/*<Group draggable>
				{/*<Rect
					width={text.width}
					height={text.fontSize}
					fill="transparent"
					visible={visible}
        />*/}
				{/* Durante el resize solo cambia la caja: hornear scale en width/height en cada frame */}
				<KonvaText
					onContextMenu={handleContextMenu}
					ref={textRef}
					x={pixelX}
					y={pixelY}
					text={text.text}
					fontSize={resolvedFontSize}
					fontFamily={text.fontFamily ?? "Montserrat"}
					fill={text.fill ?? "black"}
					stroke={text.stroke ?? "white"}
					strokeWidth={text.strokeWidth ?? 4}
					align={text.align ?? "left"}
					fillAfterStrokeEnabled={true}
					width={resolvedDims.width}
					height={resolvedDims.height}
					scaleX={text.scaleX || 1}
					scaleY={text.scaleY || 1}
					rotation={text.rotation || 0}
					draggable
					onClick={handleSelect}
					onSelect={handleSelect}
					onTap={handleSelect}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
					onTransform={(e) => {
						const node = e.target;
						const sx = node.scaleX();
						const sy = node.scaleY();
						if (sx === 1 && sy === 1) return;
						// Hornear scale en width/height para que el texto no se escale durante el drag
						const w = Math.max(20, node.width() * (sx || 1));
						const h = Math.max(14, node.height() * (sy || 1));
						node.width(w);
						node.height(h);
						node.scaleX(1);
						node.scaleY(1);
						node.getLayer()?.batchDraw();
					}}
					onDragStart={() => {
						if (onTransformStart) onTransformStart();
					}}
					onDragEnd={(e) => {
						const node = e.target;
						const nodeVisibleW = node.width() * (node.scaleX() || 1);
						const nodeVisibleH = node.height() * (node.scaleY() || 1);
						const dims = getCanvasDims();
						const stageX = node.x();
						const stageY = node.y();
						// Usar las mismas dimensiones del Stage (no DOM) para consistencia con anclajes
						if (
							stageX < -nodeVisibleW ||
							stageX > dims.w ||
							stageY < -nodeVisibleH ||
							stageY > dims.h
						) {
							onDelete();
						} else {
							const auxPage = { ...page };
							const updatedTexts = auxPage.canvasTexts.slice();
							
							// Usar hitTestBlock si está disponible (anclaje a bloques igual que stickers)
							const hit = hitTestBlock ? hitTestBlock(stageX, stageY) : null;
							
							let updatedText = {
								...updatedTexts[index],
								x: stageX,
								y: stageY,
								originalCanvasWidth: dims.w,
							};
							
							if (hit) {
								// Anclaje a bloque
								const { blockId, rect } = hit;
								const blockOffsetXPercent = rect.width !== 0
									? ((stageX - rect.left) / rect.width) * 100
									: 0;
								const blockOffsetYPercent = rect.height !== 0
									? ((stageY - rect.top) / rect.height) * 100
									: 0;
								updatedText = {
									...updatedText,
									anchorType: "block",
									anchorBlockId: blockId,
									blockOffsetXPercent,
									blockOffsetYPercent,
								};
							} else {
								// Anclaje a página (fallback)
								const anchorData = pixelToAnchor(
									stageX,
									stageY,
									nodeVisibleW,
									nodeVisibleH,
									dims.w,
									dims.h
								);
								updatedText = {
									...updatedText,
									anchorType: "page",
									anchor: anchorData.anchor,
									offsetX: dims.w > 0 ? (anchorData.offsetX / dims.w) * 100 : 0,
									offsetY: dims.h > 0 ? (anchorData.offsetY / dims.h) * 100 : 0,
									offsetInPixels: false,
									xPercent: (stageX / anchorData.canvasWidth) * 100,
									yPercent: (stageY / anchorData.canvasHeight) * 100,
								};
							}
							
							updatedTexts[index] = updatedText;
							auxPage.canvasTexts = [...updatedTexts];
							updateCurrentPage(auxPage);
						}
						// Notificar fin de transformación
						setTimeout(() => {
							if (onTransformEnd) onTransformEnd();
						}, 0);
					}}
					onTransformEnd={(e) => {
						const node = e.target;
						const scaleX = node.scaleX();
						const scaleY = node.scaleY();

						// Solo la bounding box: hornear scale en width/height y dejar scale en 1.
						// Así el texto (fontSize) no cambia; solo el tamaño de la caja.
						const newWidth = Math.max(20, node.width() * (scaleX || 1));
						const newHeight = Math.max(14, node.height() * (scaleY || 1));
						node.width(newWidth);
						node.height(newHeight);
						node.scaleX(1);
						node.scaleY(1);

						const dims = getCanvasDims();
						const stageX = node.x();
						const stageY = node.y();
						const cw = dims.w;
						const ch = dims.h;
						
						const auxPage = { ...page };
						const updatedTexts = auxPage.canvasTexts.slice();
						
						// Usar hitTestBlock si está disponible (anclaje a bloques igual que stickers)
						const hit = hitTestBlock ? hitTestBlock(stageX, stageY) : null;
						
						let updatedText = {
							...updatedTexts[index],
							x: stageX,
							y: stageY,
							width: newWidth,
							height: newHeight,
							scaleX: 1,
							scaleY: 1,
							rotation: node.rotation(),
							originalCanvasWidth: cw,
							widthPercent: cw > 0 ? (newWidth / cw) * 100 : undefined,
							heightPercent: ch > 0 ? (newHeight / ch) * 100 : undefined,
						};
						
						if (hit) {
							// Anclaje a bloque
							const { blockId, rect } = hit;
							const blockOffsetXPercent = rect.width !== 0
								? ((stageX - rect.left) / rect.width) * 100
								: 0;
							const blockOffsetYPercent = rect.height !== 0
								? ((stageY - rect.top) / rect.height) * 100
								: 0;
							updatedText = {
								...updatedText,
								anchorType: "block",
								anchorBlockId: blockId,
								blockOffsetXPercent,
								blockOffsetYPercent,
							};
						} else {
							// Anclaje a página (fallback)
							const anchorData = pixelToAnchor(
								stageX,
								stageY,
								newWidth,
								newHeight,
								cw,
								ch
							);
							updatedText = {
								...updatedText,
								anchorType: "page",
								anchor: anchorData.anchor,
								offsetX: cw > 0 ? (anchorData.offsetX / cw) * 100 : 0,
								offsetY: ch > 0 ? (anchorData.offsetY / ch) * 100 : 0,
								offsetInPixels: false,
								xPercent: (stageX / cw) * 100,
								yPercent: (stageY / ch) * 100,
							};
						}

						updatedTexts[index] = updatedText;
						auxPage.canvasTexts = [...updatedTexts];
						updateCurrentPage(auxPage);
						// Notificar fin de transformación
						setTimeout(() => {
							if (onTransformEnd) onTransformEnd();
						}, 0);
					}}
				/>
				{isSelected && (
					<Transformer
						ref={trRef}
						flipEnabled={true}
						boundBoxFunc={(oldBox, newBox) => {
							if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
								return oldBox;
							}
							return newBox;
						}}
						anchorSize={12}
						anchorCornerRadius={2}
						anchorFill="#6366F1"
						anchorStroke="#8B5CF6"
						borderStroke="#8B5CF6"
						onTransformStart={() => {
							if (onTransformStart) onTransformStart();
						}}
					/>
				)}
				{/*</Group>*/}
			</>
		);
	}
);

export default TextSticker;
